import Dialog from "@mui/material/Dialog";
import { Typography } from "@mui/material";
import React, { SyntheticEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import Grid from "@mui/material/Grid";
import { sortByStartsWith } from "dora-shared";
import { useSelector } from "react-redux";
import {
  selectPricingAgreements,
  selectProducts,
  selectProductsVisibleToDrivers,
  selectUnits,
} from "../../../ducks/data/finance/selectors";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useAppDispatch } from "../../../redux-store";
import {
  createInvoiceLine,
  updateInvoiceLine,
} from "../../../ducks/data/finance";
import {
  CreateUpdateInvoiceLine,
  InvoiceLine,
  PricingAgreement,
} from "../../../ducks/data/finance/types";

const AddEditProductModal = (props: {
  cargoDeliveryId: string;
  invoiceLine: InvoiceLine | null;
  onClose: () => void;
}) => {
  const { cargoDeliveryId, invoiceLine, onClose } = props;
  const { t } = useTranslation("stopPage");
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const allProducts = useSelector(selectProducts);
  const productsVisibleToDrivers = useSelector(selectProductsVisibleToDrivers);
  const units = useSelector(selectUnits);
  const pricingAgreements = useSelector(selectPricingAgreements);
  const [quantity, setQuantity] = useState<string | null>(
    invoiceLine?.quantity || null
  );
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    invoiceLine?.productId || null
  );
  // The specific agreement picked from the dropdown. Lines don't store which
  // agreement was applied, so this starts null when editing — the row still
  // submits its productId, and the driver can re-pick an agreement.
  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(
    null
  );
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
    invoiceLine?.unitId || null
  );

  // Each option carries the underlying product id and (optionally) the specific
  // agreement it represents, so picking it can drive that agreement's price.
  type ProductOption = {
    value: string;
    productId: string;
    name: string;
    productNumber: string;
    agreement: PricingAgreement | null;
  };

  const productsOptions = useMemo<ProductOption[]>(() => {
    return productsVisibleToDrivers
      .flatMap((product): ProductOption[] => {
        const agreements = pricingAgreements.filter(
          (pa) => pa.productId === product.id
        );
        // A product with agreements is shown as one option per agreement (no
        // plain-product entry); a product without agreements keeps a single one.
        if (agreements.length > 0) {
          return agreements.map((pa) => ({
            value: pa.id,
            productId: product.id,
            name: pa.name || product.name,
            productNumber: product.productNumber,
            agreement: pa,
          }));
        }
        return [
          {
            value: product.id,
            productId: product.id,
            name: product.name,
            productNumber: product.productNumber,
            agreement: null,
          },
        ];
      })
      .sort((p1, p2) => p1.name.localeCompare(p2.name));
  }, [productsVisibleToDrivers, pricingAgreements]);

  const optionByValue = useMemo(() => {
    const map = new Map<string, ProductOption>();
    for (const option of productsOptions) {
      map.set(option.value, option);
    }
    return map;
  }, [productsOptions]);

  const selectedProductOption = useMemo((): ProductOption | null => {
    const key = selectedAgreementId || selectedProductId;
    if (!key) return null;
    const fromOptions = optionByValue.get(key);
    if (fromOptions) return fromOptions;
    // The line's product may not be among the visible options (e.g. not visible
    // to drivers, or an agreement-only product being edited). Synthesize an
    // option so the current selection still displays.
    if (!selectedProductId) return null;
    const product = allProducts.find((p) => p.id === selectedProductId);
    if (!product) return null;
    const agreement = selectedAgreementId
      ? (pricingAgreements.find((pa) => pa.id === selectedAgreementId) ?? null)
      : null;
    return {
      value: key,
      productId: product.id,
      name: agreement?.name || product.name,
      productNumber: product.productNumber,
      agreement,
    };
  }, [
    selectedAgreementId,
    selectedProductId,
    optionByValue,
    allProducts,
    pricingAgreements,
  ]);

  const buildLineBody = (): CreateUpdateInvoiceLine => {
    const product = allProducts.find((p) => p.id === selectedProductId);
    const agreement = selectedAgreementId
      ? (pricingAgreements.find((pa) => pa.id === selectedAgreementId) ?? null)
      : null;
    // The modal has no description field; it's derived. Keep the line's existing
    // description on a same-product edit where no agreement was re-picked, so a
    // quantity-only edit doesn't silently rewrite it.
    const keepExistingDescription =
      !!invoiceLine &&
      invoiceLine.productId === selectedProductId &&
      !selectedAgreementId;
    return {
      quantity: quantity ? quantity.replace(",", ".") : "",
      productId: selectedProductId,
      unitId: selectedUnitId,
      description: keepExistingDescription
        ? invoiceLine!.description
        : agreement?.name || product?.name || null,
      pricingAgreementId: selectedAgreementId,
    };
  };

  const onAddClick = async (e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    dispatch(
      createInvoiceLine({
        deliveryId: cargoDeliveryId,
        line: buildLineBody(),
      })
    )
      .unwrap()
      .then(() => onClose())
      .catch(() => {
        // Already reported and shown by the thunk; keep the modal open.
      })
      .finally(() => setLoading(false));
  };

  const onSaveClick = async (e: SyntheticEvent) => {
    e.preventDefault();
    setLoading(true);
    dispatch(
      updateInvoiceLine({
        deliveryId: cargoDeliveryId,
        lineId: invoiceLine!.id,
        line: buildLineBody(),
      })
    )
      .unwrap()
      .then(() => onClose())
      .catch(() => {
        // Already reported and shown by the thunk; keep the modal open.
      })
      .finally(() => setLoading(false));
  };

  const onSelectOption = (option: ProductOption | null) => {
    if (!option) {
      setSelectedProductId(null);
      setSelectedAgreementId(null);
      setSelectedUnitId(null);
      return;
    }
    setSelectedProductId(option.productId);
    setSelectedAgreementId(option.agreement?.id ?? null);
    const product = allProducts.find((p) => p.id === option.productId);
    setSelectedUnitId(option.agreement?.unitId || product?.unitId || null);
    if (option.agreement?.unitNumber) {
      setQuantity(option.agreement.unitNumber.toString());
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <div className="px-6 py-4">
        <Typography variant="h6" component="h2" pt={2}>
          {t("addProduct.heading")}
        </Typography>

        <form onSubmit={invoiceLine ? onSaveClick : onAddClick}>
          <Grid columns={12}>
            <Autocomplete
              getOptionLabel={(option) => option.name || ""}
              filterOptions={(options, state) => {
                const searchLower = state.inputValue;
                return options
                  .filter(
                    (p) =>
                      p.name.toLowerCase().includes(searchLower) ||
                      p.productNumber.includes(searchLower)
                  )
                  .sort(sortByStartsWith(searchLower, "name", "productNumber"));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  autoFocus
                  margin="dense"
                  label={t("addProduct.product")}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <span>{option.name}&nbsp;•&nbsp;</span>
                  <span style={{ color: "var(--gray-400)" }}>
                    {option.productNumber}
                  </span>
                </li>
              )}
              options={productsOptions}
              value={selectedProductOption}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              onChange={(_, v) => onSelectOption(v)}
            />
          </Grid>
          <Grid columns={12}>
            <Autocomplete
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin="dense"
                  label={t("addProduct.unit")}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>{option.name}</li>
              )}
              options={units}
              value={units.find((unit) => unit.id === selectedUnitId) || null}
              onChange={(_, v) => setSelectedUnitId(v?.id || null)}
            />
          </Grid>
          <Grid columns={12}>
            <TextField
              label={t("addProduct.quantity")}
              margin="dense"
              fullWidth
              value={quantity}
              required
              type="number"
              InputProps={{
                componentsProps: {
                  input: {
                    inputMode: "decimal",
                  },
                },
              }}
              InputLabelProps={{ shrink: !!quantity }}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </Grid>
          <Grid columns={12}>
            <div className="flex gap-2 justify-between w-100 mt-4">
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={onClose}
              >
                {t("notes.backButtonLabel")}
              </Button>
              <LoadingButton
                loading={loading}
                disabled={!selectedProductId}
                type="submit"
                fullWidth
                size="large"
                variant="contained"
              >
                <span>
                  {invoiceLine
                    ? t("addProduct.saveButtonLabel")
                    : t("addProduct.addButtonLabel")}
                </span>
              </LoadingButton>
            </div>
          </Grid>
        </form>
      </div>
    </Dialog>
  );
};

export default AddEditProductModal;
